import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpBackend, provideHttpClient, withJsonpSupport } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, withComponentInputBinding, withPreloading } from '@angular/router';
import { ApiPartnerEnum, provideRdvClient, RdvTranslateLoader } from '@claas-rdv/rdv-client';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore, Store } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { OKTA_AUTH, provideOktaAuth, withOktaConfig } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { forkJoin, map } from 'rxjs';
import { deriveUserLanguage, InternationalizationService, provideSharedUi, withNotifier, withProfileCountry } from '@soff/shared-ui';
import { withSavedViews } from '@soff/shared-ui/saved-views';
import { provideTsc, withPageTitle } from '@soff/tsc-ui/config';
import { environment } from 'src/environments/environment';
import { routes as APP_ROUTES } from './app.routes';
import { TaskIdentPipe } from './features/field-service/pipes/task-ident.pipe';
import { WorkOrderIdentPipe } from './features/field-service/pipes/workorder-ident.pipe';
import { PaginatorI18n } from './shared/components/PaginatorI18n';
import { MetricsErrorHandler } from './shared/handler/metrics-error.handler';
import { DtcPipe } from './shared/pipe/dtc.pipe';
import { TitleService } from './shared/services/title.service';
import { effects, reducers } from './shared/state';
import { AccountSelectors } from './shared/state/account';
import { StoreSoffNotifier } from './soff-notifier';
import { soffTscConfigFactory } from './soff-tsc-config';
import { soffViewFilterContextFactory } from './soff-view-filter-context';

//const { CLIENT_ID, ISSUER, OKTA_TESTING_DISABLEHTTPSCHECK } = process.env;
export const oauthConfig = {
  clientId: environment.okta.clientId,
  issuer: environment.okta.issuer,
  redirectUri: typeof environment.okta.redirectUri === 'function' ? environment.okta.redirectUri() : environment.okta.redirectUri,
  scopes: ['openid', 'profile', 'email'],
  pkce: true,
  testing: {
    disableHttpsCheck: true,
  },
};

export const oktaAuth = new OktaAuth(oauthConfig);

/**
 * Custom TranslateLoader that merges the app's HTTP-loaded translations
 * with the RDV library's inline translations.
 *
 * This is necessary because ngx-translate's loadAndCompileTranslations()
 * overwrites the entire translations object for a language (when extend=false),
 * which discards any translations previously merged via setTranslation().
 * The RDV library's provideRdvClient() merges its translations in an
 * APP_INITIALIZER and subscribes to onTranslationChange for re-merging,
 * but loadAndCompileTranslations does NOT emit onTranslationChange —
 * it only emits onDefaultLangChange or onLangChange. So the RDV translations
 * are lost after the HTTP loader finishes.
 *
 * By combining both loaders here, the RDV translations are included in
 * every load cycle and can never be overwritten.
 */
class CombinedTranslateLoader implements TranslateLoader {
  private httpLoader: MultiTranslateHttpLoader;
  private rdvLoader = new RdvTranslateLoader();

  constructor(httpBackend: HttpBackend) {
    this.httpLoader = new MultiTranslateHttpLoader(httpBackend, [
      { prefix: './assets/i18n/global/', suffix: '.json' },
      { prefix: './assets/i18n/machines/', suffix: '.json' },
      { prefix: './assets/i18n/field-service/', suffix: '.json' },
      { prefix: './assets/i18n/tickets/', suffix: '.json' },
      { prefix: './assets/i18n/customers/', suffix: '.json' },
      { prefix: './assets/i18n/employees/', suffix: '.json' },
      { prefix: './assets/i18n/service-vans/', suffix: '.json' },
      { prefix: './assets/i18n/incoming-topics/', suffix: '.json' },
      { prefix: './assets/i18n/tsc-admin/', suffix: '.json' },
      { prefix: './assets/i18n/contracts/', suffix: '.json' },
      { prefix: './assets/i18n/log-messages/', suffix: '.json' },
      { prefix: './assets/i18n/technical-documentation/', suffix: '.json' },
      { prefix: './assets/i18n/account/', suffix: '.json' },
      { prefix: './assets/i18n/notes/', suffix: '.json' },
      { prefix: './assets/i18n/intro/', suffix: '.json' },
      { prefix: './assets/i18n/telemetry/', suffix: '.json' },
      { prefix: './assets/i18n/cai/', suffix: '.json' },
    ]);
  }

  getTranslation(lang: string) {
    return forkJoin([this.httpLoader.getTranslation(lang), this.rdvLoader.getTranslation(lang)]).pipe(
      map(([appTranslations, rdvTranslations]) => ({
        ...appTranslations,
        ...rdvTranslations,
      }))
    );
  }
}

// TODO muss sicher noch anders importiert / registriert werden
const PIPES = [TaskIdentPipe, WorkOrderIdentPipe, DecimalPipe, DatePipe, DtcPipe];

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ErrorHandler, useClass: MetricsErrorHandler },
    provideRouter(
      APP_ROUTES,
      withPreloading(PreloadAllModules),
      withComponentInputBinding()
      //withDebugTracing(),
    ),
    // globally translate all list paginators
    {
      provide: MatPaginatorIntl,
      deps: [TranslateService],
      useFactory: (translateService: TranslateService) => new PaginatorI18n(translateService).getPaginatorIntl(),
    },
    provideHttpClient(withJsonpSupport()),
    provideAnimations(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    importProvidersFrom([
      TranslateModule.forRoot({
        defaultLanguage: environment.defaultLanguage,
        loader: {
          provide: TranslateLoader,
          useFactory: (httpBackend: HttpBackend) => new CombinedTranslateLoader(httpBackend),
          deps: [HttpBackend],
        },
      }),
    ]),
    provideOktaAuth(withOktaConfig({ oktaAuth })),
    provideStore(reducers),
    provideRouterStore(),
    provideStoreDevtools(),
    provideEffects(effects),
    // Shared infrastructure config + SOFF adapters for its ports (see docs/plans/tsc-ui-library-extraction.md §4.1)
    provideSharedUi(
      () => {
        const okta = inject(OKTA_AUTH);
        return {
          api: {
            baseUrl: environment.apiBaseUrl,
            getToken: () => okta.getIdToken() ?? null,
            defaultHeaders: { 'x-soff-workshop-filter': 'on' },
          },
          defaultLanguage: environment.defaultLanguage,
          assetsBaseUrl: 'assets/',
        };
      },
      withNotifier(StoreSoffNotifier),
      withSavedViews(soffViewFilterContextFactory),
      withProfileCountry(() =>
        toSignal(
          inject(Store)
            .select(AccountSelectors.getUserProfile)
            .pipe(map(userProfile => userProfile?.country))
        )
      )
    ),
    // Ticket UI config + SOFF adapters for its ports (§4.2)
    provideTsc(soffTscConfigFactory, withPageTitle(TitleService)),
    PIPES,
    provideRdvClient({
      partnerSystem: ApiPartnerEnum.SOFF,
      apiBaseUrl: environment.apiBaseUrl,
      language: deriveUserLanguage(environment.defaultLanguage),
      standaloneUrl: environment.rdvBaseUrl,
    }),
    { provide: LOCALE_ID, deps: [InternationalizationService], useFactory: (i18n: InternationalizationService) => i18n.getUserLocale() },
  ],
};
